export const FEATURE_FLAGS = {
  RESIDENTIAL_BOOKING_VIEW_V2: 'residential-booking-view-v2',
  RESIDENTIAL_BOOKING_VIEW_V2_SANDBOX: 'residential-booking-view-v2-sandbox',
  GUEST_MODE_LANDING_PAGE: 'guest-mode-landing-page',
  GUEST_MODE_LANDING_PAGE_SANDBOX: 'guest-mode-landing-page-sandbox',
  PM_DEMO_LANDING_PAGE: 'pm-demo-landing-page',
  PM_DEMO_LANDING_PAGE_SANDBOX: 'pm-demo-landing-page-sandbox',
  PM_DEMO_ACCOUNT_MANAGER: 'pm-demo-account-manager',
  PM_DEMO_ACCOUNT_MANAGER_SANDBOX: 'pm-demo-account-manager-sandbox',
  PM_CREATE_ACCOUNT_PHONE: 'pm-create-account-phone',
  PM_CREATE_ACCOUNT_PHONE_SANDBOX: 'pm-create-account-phone-sandbox',
  PM_SCHEDULE_CALL_SELECTION: 'pm-schedule-call-selection',
  PM_SCHEDULE_CALL_SELECTION_SANDBOX: 'pm-schedule-call-selection-sandbox',
  PM_SCHEDULE_CALL_FIRST: 'pm-schedule-call-first',
  PM_SCHEDULE_CALL_FIRST_SANDBOX: 'pm-schedule-call-first-sandbox',
  PM_DEMO_TOUR: 'pm-demo-tour',
  PM_DEMO_TOUR_SANDBOX: 'pm-demo-tour-sandbox',
  CLIENT_APP_MINIMUM_VERSION: 'client-app-minimum-version',
  CLIENT_APP_MINIMUM_VERSION_SANDBOX: 'client-app-minimum-version-sandbox',
} as const;

export type FeatureFlagKey = typeof FEATURE_FLAGS[keyof typeof FEATURE_FLAGS];

export const RESIDENTIAL_BOOKING_VIEW_V2_VARIANTS = [
  'control',
  'old-view-uncollapsed',
  'new-view-collapsed',
  'new-view-uncollapsed',
] as const;

export type ResidentialBookingViewV2Variant = typeof RESIDENTIAL_BOOKING_VIEW_V2_VARIANTS[number];

export const BOOK_PAGE_VARIANTS = [
  'cleaning-any-time',
  'all-categories-any-time',
  'cleaning-select-time',
  'all-categories-select-time',
] as const;

export type BookPageVariant = typeof BOOK_PAGE_VARIANTS[number];

export const GUEST_LANDING_VARIANTS = ['control', 'overview'] as const;
export type GuestLandingVariant = typeof GUEST_LANDING_VARIANTS[number];

export const GUEST_LANDING_FLAG_KEYS: Record<string, string> = {
  sandbox3: 'guest-mode-landing-page-sandbox',
  production: 'guest-mode-landing-page',
};

// Property-manager/host demo landing page A/B test: which page a demo visitor lands
// on after the "Preparing Demo" entrance. 'control' keeps the Strategy/Income overview.
export const PM_DEMO_LANDING_VARIANTS = ['control', 'schedule', 'properties', 'chat', 'optimizer'] as const;
export type PmDemoLandingVariant = typeof PM_DEMO_LANDING_VARIANTS[number];

// Property-manager/host demo "Meet Your Account Manager" A/B test (funnel begins after
// account creation; KPI = scheduling a setup call). control = current MYAM page;
// salesy-v1 / salesy-v2 / salesy-v3 = new MYAM page designs; skip-to-schedule = skip MYAM
// and land on the Schedule Setup Call page.
export const PM_DEMO_ACCOUNT_MANAGER_VARIANTS = ['control', 'salesy-v1', 'salesy-v2', 'salesy-v3', 'skip-to-schedule'] as const;
export type PmDemoAccountManagerVariant = typeof PM_DEMO_ACCOUNT_MANAGER_VARIANTS[number];

// PM demo create-account phone-number A/B test: 'show-phone' adds a required phone field
// to the right of the email field on the create-account page; 'control' shows no phone field.
export const PM_CREATE_ACCOUNT_PHONE_VARIANTS = ['control', 'show-phone'] as const;
export type PmCreateAccountPhoneVariant = typeof PM_CREATE_ACCOUNT_PHONE_VARIANTS[number];

// PM demo schedule-call selection A/B test: 'auto-learn' hides the "what do you want to do"
// goal radios and loads the Learn-about-TIDY Calendly directly; 'control' shows the radios.
export const PM_SCHEDULE_CALL_SELECTION_VARIANTS = ['control', 'auto-learn'] as const;
export type PmScheduleCallSelectionVariant = typeof PM_SCHEDULE_CALL_SELECTION_VARIANTS[number];

// PM demo schedule-call-first A/B test: 'schedule-call-first' re-orders the demo conversion
// funnel so the indicator nudges the user to schedule a call before creating an account (and
// flips to "Create Account" after they schedule, tracked by the pm_scf_scheduled client flag);
// 'control' keeps the existing "Get Started" → create-account-first flow.
export const PM_SCHEDULE_CALL_FIRST_VARIANTS = ['control', 'schedule-call-first'] as const;
export type PmScheduleCallFirstVariant = typeof PM_SCHEDULE_CALL_FIRST_VARIANTS[number];

// PM demo guided-walkthrough A/B test: 'guided-tour' shows the guide dock that walks the demo
// visitor through all five demo pages after the welcome card; 'control' is the plain demo with
// no walkthrough. Desktop only — mobile visitors are never assigned a variant (see
// PmDemoTourService), so this flag has no meaning on mobile.
export const PM_DEMO_TOUR_VARIANTS = ['control', 'guided-tour'] as const;
export type PmDemoTourVariant = typeof PM_DEMO_TOUR_VARIANTS[number];

export const FEATURE_FLAG_DEFAULTS: Record<string, string | boolean | null> = {
  [FEATURE_FLAGS.RESIDENTIAL_BOOKING_VIEW_V2]: 'control',
  [FEATURE_FLAGS.RESIDENTIAL_BOOKING_VIEW_V2_SANDBOX]: 'control',
  [FEATURE_FLAGS.GUEST_MODE_LANDING_PAGE]: 'control',
  [FEATURE_FLAGS.GUEST_MODE_LANDING_PAGE_SANDBOX]: 'control',
  [FEATURE_FLAGS.PM_DEMO_LANDING_PAGE]: 'control',
  [FEATURE_FLAGS.PM_DEMO_LANDING_PAGE_SANDBOX]: 'control',
  [FEATURE_FLAGS.PM_DEMO_ACCOUNT_MANAGER]: 'control',
  [FEATURE_FLAGS.PM_DEMO_ACCOUNT_MANAGER_SANDBOX]: 'control',
  [FEATURE_FLAGS.PM_CREATE_ACCOUNT_PHONE]: 'control',
  [FEATURE_FLAGS.PM_CREATE_ACCOUNT_PHONE_SANDBOX]: 'control',
  [FEATURE_FLAGS.PM_SCHEDULE_CALL_SELECTION]: 'control',
  [FEATURE_FLAGS.PM_SCHEDULE_CALL_SELECTION_SANDBOX]: 'control',
  [FEATURE_FLAGS.PM_SCHEDULE_CALL_FIRST]: 'control',
  [FEATURE_FLAGS.PM_SCHEDULE_CALL_FIRST_SANDBOX]: 'control',
  [FEATURE_FLAGS.PM_DEMO_TOUR]: 'control',
  [FEATURE_FLAGS.PM_DEMO_TOUR_SANDBOX]: 'control',
};
